const AGENT_BASE_URL = 'http://127.0.0.1:17845'

/**
 * Talks to the optional local Windows background agent (separate repo:
 * viertel-lieferservice-windows-agent) that enables dialog-free receipt printing,
 * an acoustic alarm for new orders while the screen is locked, and single-instance
 * browser-window focusing on unlock. All calls fail silently when the agent isn't
 * running/installed, so the app keeps working without it.
 */
export class WindowsAgentClient {

	static probe = () => fetch(`${AGENT_BASE_URL}/health`, { signal: AbortSignal.timeout(300) })
		.then(response => response.ok)
		.catch(() => false)

	static authorize = (accessToken, storeIds) => fetch(`${AGENT_BASE_URL}/authorize`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ accessToken, storeIds }),
		signal: AbortSignal.timeout(2000)
	})
		.catch(() => {})

	/**
	 * Reports whether the webapp itself is currently ready to ring for new orders (open,
	 * logged in, WebSocket connected), so the agent knows when it must ring instead - see
	 * WindowsAgentSoundReadyBridge. Logs the outcome as console.info, since the app is
	 * expected to run without the agent too (e.g. in a normal browser tab or on a phone) -
	 * a failed report just means no agent is present, not an error.
	 * @param {*} ready
	 */
	static reportSoundReady = ready => fetch(`${AGENT_BASE_URL}/soundReady`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ ready }),
		signal: AbortSignal.timeout(1500)
	})
		.then(response => {
			if (response.ok) {
				console.info(`WindowsAgentClient: sound-ready status reported to agent, ready=${ready}`)
			}
			else {
				console.info(`WindowsAgentClient: agent rejected sound-ready report (HTTP ${response.status}), ready=${ready}`)
			}
		})
		.catch(() => {
			console.info(`WindowsAgentClient: could not report sound-ready status (no agent running?), ready=${ready}`)
		})

	/**
	 * @param {*} imageDataUrl PNG data URL (e.g. canvas.toDataURL('image/png'))
	 * @param {*} widthPx
	 * @param {*} heightPx
	 * @returns Promise<boolean> whether the agent accepted and printed the receipt
	 */
	static printBitmap = (imageDataUrl, widthPx, heightPx) => {
		const imageBase64 = imageDataUrl.split(',')[1]
		return fetch(`${AGENT_BASE_URL}/print`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ imageBase64, widthPx, heightPx }),
			signal: AbortSignal.timeout(5000)
		})
			.then(response => response.ok)
			.catch(() => false)
	}

}
